import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { Button }from 'reactstrap';
import Body from '../Components/layout/Body';
import { SUBTABS, GOALS_STEPS } from './ConstPaths';

const REPORT_HELP = `
  <div>
    <p>We've provided a couple of common next steps here for you to choose from.</p>
    <p><strong>Choose a new Priority Activity.</strong> This will send you to the Gather Info / Choose Activities page, so you can modify the client's goals as needed and then choose a new Priority Activity. 
    Usually, you would do this when you are ready to focus on a new Priority Activity: perhaps you've found a solution for accessing the current Priority Activity, so you're ready to move on. 
    Or maybe the current Priority Activity has proven to be challenging, and you want to try a different activity that might yield some success. 
    </p>
    <p><strong>Create a report.</strong> The report includes:</p>  
    <ul>
      <li>All of the info you entered for the client’s profile (like goals & needs, as well as abilities)</li>
      <li>A summary of the Test-drives</li>
      <li>A summary of decision-making: this lists the Prospects still under consideration, as well as Picks that you’ve identified for next steps. </li>
    </ul>
    <p>In every section of the report, you can add a note, if you want. 
      You can also print the report to a PDF file or a printer, using your browser's print function. (Eventually we hope to improve this, but for now it provides basic printing functionality.)
    </p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  picks: PropTypes.arrayOf(PropTypes.object),
  prospects: PropTypes.arrayOf(PropTypes.object),
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  picks: [],
  prospects: []
};

//content sub-component with unique content for this page
function ReviewTabNextstepsGenerateReportContent(props) {
  const {
    firstName,
    reportRoute,
    picks,
    prospects
  } = props;

  let history = useHistory();
  const curURL = new URL(window.location.href);


  let priorityRoute = SUBTABS.GOALS + "/" + GOALS_STEPS.ACTIVITIES;

  //singular / plural finessing. only displayed if there are any picks or prospects.
  const picksString = (picks.length === 1) ? "a Pick" : "some Picks";
  const alsoString = (picks.length === 1) ? "also" : "";
  const prospectsString = (prospects.length === 1) ? "a Prospect" : "some Prospects";

  //TODO conditional info about whether Picks were made, etc. (see wireframe)
  return(
    <>
    <p><strong>Great work!</strong> You've gathered some new information and taken steps toward enhancing {firstName}'s access.</p>
    <p>
      {(picks.length > 0) 
        ? <span>You've chosen {picksString} to move forward with.</span>
        : <span>You haven't made any Picks yet, but you can do that later, after you've arrived at something you want to move forward with. 
            (And once you've got a Pick or two, you can crosscheck them against the goals and needs you defined, and recommend some next steps. <span className="fa fa-smile-o"></span>)
          </span>
      }
      <span> </span>
      {(prospects.length > 0) 
        ? <span>You've {alsoString} got {prospectsString} in your list if you want to explore further.</span>
        : <span></span>
      }
    </p>
    <p>Future sessions with Access Navigator can give you additional information if you need it.</p>
    <hr />
    <p>You can continue on now if you're ready to focus on a new Priority Activity.</p>
    <p>
      <Button color="primary" onClick={() => {
        history.push({
          pathname: priorityRoute,
          search: curURL.search
        });
      }
      }>Choose a new Priority Activity</Button>
    </p>
    <p>Or just use the Test-drive tab above to do some more Test-drives without changing the Priority Activity.</p>
    <hr />
    <p>Create a report documenting what you've accomplished so far along with next steps:</p>
    <p>
      <Button color="primary" onClick={() => {
          history.push({
            pathname: reportRoute,
            search: curURL.search
          });
        }
      }>Create a report</Button>
    </p>
    </>
  )
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function ReviewTabNextstepsGenerateReport(props) {
  const {
    firstName,
    pronouns,
    picks,
    prospects,
    reportRoute,
    onContinue
  } = props;

  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-thumbs-o-up",
    pageSymbolClassName: "aa-section-symbol review",
    title: `Choose your next step`,
    subTitle: ``,
    learnMore: {linkText: "Learn More about choosing next steps and reporting",
                title: "Next steps and reporting",
                content: REPORT_HELP
                },
    nextRoute: "", 
    sidebar: false,
    continueName: 'Done'
  }

  console.log("Rendering ReviewTabNextstepsGenerateReport");
  
  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <ReviewTabNextstepsGenerateReportContent 
          firstName={firstName}
          picks={picks}
          prospects={prospects}
          reportRoute={reportRoute}
      />} 
      onContinue={onContinue} 
    />
    </>
  );
}

ReviewTabNextstepsGenerateReport.propTypes = propTypes;
ReviewTabNextstepsGenerateReport.defaultProps = defaultProps;

export default ReviewTabNextstepsGenerateReport;